import { Component, h, Prop, Element } from '@stencil/core';
import { setDataAttributesOnFormFields } from '../component-utils/formUtils';

/**
 * The COMPNAME is for adding a dropdown field to a form
 *
 */
@Component({
  tag: 'aafp-dropdown',
  shadow: false,
})
export class AafpDropdown {
  @Prop({ reflect: false }) actionId!: string;
  @Prop({ reflect: false }) label!: string;
  @Prop({ reflect: false }) name!: string;
  @Prop({ reflect: false }) required: boolean = false;
  @Prop() errorMessage: string;
  @Prop({ reflect: false }) helperText: string;
  /**
   * For passing error classes from jqueryval
   */
  @Prop({ mutable: true }) additionalClasses: Object = {};
  /**
   * For adding data-attributes to form inputs. Must be in "name=value|name=value" format.
   */
  @Prop() dataAttr: string;

  @Element() selectEl: HTMLElement;

  DROPDOWN_ACTIVE_CLASS: string = 'adl-dropdown__select--active';

  /**
   * Checks the value of the element passed into the select.
   * Adds the active class to the select if select-value exists, and removes
   * active class if the value is empty
   * @param selectEl - select element within TextInput component
   */
  updateActiveClassOnSelectValue(selectEl: HTMLSelectElement | null): void {
    if (!selectEl) return;
    const selectElHasValue = Boolean(selectEl.value);
    selectElHasValue
      ? selectEl.classList.add(this.DROPDOWN_ACTIVE_CLASS)
      : selectEl.classList.remove(this.DROPDOWN_ACTIVE_CLASS);
  }

  /**
   * Add the active class to the element provided
   * @param selectEl - select element clicked onFocus
   */
  addActiveClassOnFocus(selectEl: HTMLSelectElement): void {
    if (!selectEl) return;

    selectEl.classList.add(this.DROPDOWN_ACTIVE_CLASS);
  }

  /**
   * Stencil lifecycle method executes post-render;
   * calls update updateActiveClassOnSelectValue if initialValue is valid
   *
   */
  componentDidLoad() {
    const select: HTMLSelectElement | null = this.selectEl.querySelector('select');
    this.updateActiveClassOnSelectValue(select || null);

    if (this.dataAttr && select) {
      setDataAttributesOnFormFields(select, this.dataAttr);
    }
  }

  render() {
    // Objects created to avoid typing issues
    const labelAttrs = { for: this.name };
    const defaultErrorMessage = this.errorMessage || this.helperText;
    const styleClasses = this.additionalClasses;

    return (
      <div class="adl-dropdown adl-text-color--darkest adl-body">
        <select
          name={this.name}
          id={this.actionId}
          onFocus={(e: UIEvent) => this.addActiveClassOnFocus(e.target as HTMLSelectElement)}
          onBlur={(e: UIEvent) => this.updateActiveClassOnSelectValue(e.target as HTMLSelectElement)}
          class="adl-dropdown__select adl-background-color--lightest"
          required={this.required}
          {...styleClasses}
        >
          <option value=""></option>
          <slot />
        </select>
        <label class="adl-dropdown__label" {...labelAttrs}>
          {this.label}
        </label>
        <i class="adl-dropdown__arrow--closed adl-dropdown__arrow material-icons">arrow_drop_down</i>
        <i class="adl-dropdown__arrow--open adl-dropdown__arrow material-icons">arrow_drop_up</i>
        <i class="adl-text-input__error-icon adl-text-input__icon material-icons">error</i>
        <i class="adl-text-input__valid-icon adl-text-input__icon material-icons">done</i>
        <p class="adl-text-input__helper-text adl-body-little adl-text-color--grey">{this.helperText}</p>
        {defaultErrorMessage && <p class="adl-text-input__error-text adl-body-little">{defaultErrorMessage}</p>}
      </div>
    );
  }
}
